import { useEffect, useMemo, useState } from 'react';
import useMediaQuery from '../../hooks/useMediaQuery';
import { formatDuration } from '../../utils/timeUtils';
import styles from './ProductInfoChips.module.css';

const ProductInfoChips = ({ item, intervalMs = 4000, ensemble = false, parts = false, performers = false, difficulty = false, level = false, numberofpages = false, duration = false }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [pairIndex, setPairIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  const truncateText = (text, maxLength = 16) => {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const infoChips = useMemo(() => {
    if (!item) return [];

    const chips = [];

    if (item.ensemble && ensemble) {
      chips.push(
        <div key="ensemble" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-music-icon lucide-music ${styles.icon}`} aria-label="Ensemble"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
          <span>{isMobile ? truncateText(item.ensemble) : item.ensemble}</span>
        </div>
      );
    }

    if (item.parts && parts) {
      chips.push(
        <div key="parts" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-users-icon lucide-users ${styles.icon}`} aria-label="Parts"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><path d="M16 3.128a4 4 0 0 1 0 7.744" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><circle cx="9" cy="7" r="4" /></svg>
          <span>{isMobile ? truncateText(item.parts) : item.parts}</span>
        </div>
      );
    }

    // Recommended Number of performers (if provided separately)
    if (item.recommendednumber && performers) {
      const count = item.recommendednumber;
      const label = count === 1 ? 'performer' : 'performers';
      chips.push(
        <div key="performers" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-user-icon lucide-user ${styles.icon}`} aria-label="Performers"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>          <span>{count} {label}</span>
        </div>
      );
    }

    if (item.difficulty && difficulty) {
      chips.push(
        <div key="difficulty" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-chart-column-icon lucide-chart-column ${styles.icon}`} aria-label="Difficulty"><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></svg>
          <span>{isMobile ? truncateText(item.difficulty) : item.difficulty}</span>
        </div>
      );
    }

    if (item.level && level) {
      chips.push(
        <div key="level" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-book-icon lucide-book ${styles.icon}`} aria-label="Level"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" /></svg>
          <span>{isMobile ? truncateText(item.level) : item.level}</span>
        </div>
      );
    }

    if (item.numberofpages && numberofpages) {
      chips.push(
        <div key="pages" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-file-icon lucide-file ${styles.icon}`} aria-label="Pages"><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /><path d="M14 2v5a1 1 0 0 0 1 1h5" /></svg>
          <span>{item.numberofpages} {item.numberofpages > 1 ? 'pages' : 'page'}</span>
        </div>
      );
    }

    if (item.duration && duration) {
      chips.push(
        <div key="duration" className={styles.item}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-clock-icon lucide-clock ${styles.icon}`} aria-label="Duration"><path d="M12 6v6l4 2" /><circle cx="12" cy="12" r="10" /></svg>
          <span>{formatDuration(item.duration)}</span>
        </div>
      );
    }

    return chips;
  }, [item, ensemble, parts, performers, difficulty, level, numberofpages, duration, isMobile]);

  const pairs = useMemo(() => {
    const out = [];
    for (let i = 0; i < infoChips.length; i += 2) {
      out.push(infoChips.slice(i, i + 2));
    }
    return out.length ? out : [[]];
  }, [infoChips]);

  useEffect(() => {
    setPairIndex(0);
  }, [isMobile, infoChips.length]);

  useEffect(() => {
    if (!isMobile || pairs.length <= 1) return;
    const id = setInterval(() => {
      setIsFading(true);
      const timeout = setTimeout(() => {
        setPairIndex((i) => (i + 1) % pairs.length);
        setIsFading(false);
      }, Math.min(200, Math.max(100, Math.floor(intervalMs * 0.25))));
      return () => clearTimeout(timeout);
    }, intervalMs);
    return () => clearInterval(id);
  }, [isMobile, pairs.length, intervalMs]);

  return (
    <div className={styles.section}>
      {isMobile ? (
        <div className={`${styles.mobileCarousel} ${isFading ? styles.fadeOut : ''}`}>
          {pairs[pairIndex].map((chip) => chip)}
        </div>
      ) : infoChips}
    </div>
  );
};

export default ProductInfoChips;
